import React, { useEffect, useState } from 'react';
import { Alert, Chip, Stack, Typography } from '@mui/material';
import { Edit as EditIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import { EnhancedTable, type TableColumn, type TableAction } from '../ui/EnhancedTable';
import type { EnterprisePackageData } from '@cnapse/shared/schemas.js';
import { providerEnterpriseService } from '../../services/ProviderEnterpriseService';
import * as dt from '@cnapse/shared/data.types.js';

interface EnterprisePackagesTableProps {
  refreshTrigger?: number;
  onAdd?: () => void;
  addLabel?: string;
  onEdit?: (pkg: EnterprisePackageData) => void;
}

const EnterprisePackagesTable: React.FC<EnterprisePackagesTableProps> = ({
  refreshTrigger,
  onAdd,
  addLabel,
  onEdit,
}) => {
  const [packages, setPackages] = useState<EnterprisePackageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const loadPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerEnterpriseService.listEnterprisePackages({ active: true });
      setPackages(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load enterprise packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPackages();
  }, [refreshTrigger]);

  const filteredPackages = packages.filter((pkg) => {
    if (filters.kind && pkg.kind !== filters.kind) {
      return false;
    }
    if (filters.assigned === 'yes' && !pkg.assignedSubscriptionId) {
      return false;
    }
    if (filters.assigned === 'no' && pkg.assignedSubscriptionId) {
      return false;
    }
    if (searchTerm) {
      const haystack = [pkg.name, pkg.tenantName ?? '', pkg.internalNotes ?? ''].join(' ').toLowerCase();
      if (!haystack.includes(searchTerm.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const formatDate = (value: Date | string | null): string => {
    if (!value) {
      return '—';
    }
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleArchive = async (pkg: EnterprisePackageData) => {
    if (
      !window.confirm(
        `Archive package "${pkg.name}"?\n\nAssigned packages cannot be archived.`,
      )
    ) {
      return;
    }
    try {
      await providerEnterpriseService.archiveEnterprisePackage(pkg.packageId);
      await loadPackages();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive package');
    }
  };

  const columns: TableColumn<EnterprisePackageData>[] = [
    {
      field: 'name',
      headerName: 'Package',
      flex: 1.4,
      minWidth: 200,
      sortable: true,
      renderCell: (row) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary">
              {row.description}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'kind',
      headerName: 'Kind',
      width: 110,
      renderCell: (row) => (
        <Chip
          size="small"
          label={row.kind === dt.ENTERPRISE_PACKAGE_KIND.Pilot ? 'Pilot' : row.kind === dt.ENTERPRISE_PACKAGE_KIND.Annual ? 'Annual' : 'Amendment'}
          color={
            row.kind === dt.ENTERPRISE_PACKAGE_KIND.Pilot
              ? 'info'
              : row.kind === dt.ENTERPRISE_PACKAGE_KIND.Annual
                ? 'primary'
                : 'secondary'
          }
          variant="outlined"
        />
      ),
    },
    {
      field: 'tenantName',
      headerName: 'Linked tenant',
      flex: 1,
      minWidth: 140,
      renderCell: (row) => row.tenantName ?? 'Any tenant',
    },
    {
      field: 'trialEndsAt',
      headerName: 'Period',
      flex: 1.2,
      minWidth: 180,
      renderCell: (row) =>
        row.kind === dt.ENTERPRISE_PACKAGE_KIND.Pilot
          ? `${formatDate(row.trialStartsAt)} → ${formatDate(row.trialEndsAt)}`
          : `${formatDate(row.contractPeriodStart)} → ${formatDate(row.contractPeriodEnd)}`,
    },
    {
      field: 'assignedSubscriptionId',
      headerName: 'Status',
      width: 120,
      renderCell: (row) =>
        row.assignedSubscriptionId ? (
          <Chip size="small" label="Assigned" color="success" />
        ) : (
          <Chip size="small" label="Available" variant="outlined" />
        ),
    },
    {
      field: 'internalNotes',
      headerName: 'Internal notes',
      flex: 1.2,
      minWidth: 160,
      renderCell: (row) => row.internalNotes ?? '—',
    },
  ];

  const actions: TableAction<EnterprisePackageData>[] = [
    {
      label: 'Edit',
      icon: <EditIcon fontSize="small" />,
      onClick: (row) => onEdit?.(row),
    },
    {
      label: 'Archive',
      icon: <ArchiveIcon fontSize="small" />,
      onClick: (row) => void handleArchive(row),
      color: 'error',
      disabled: (row) => !!row.assignedSubscriptionId,
    },
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <EnhancedTable<EnterprisePackageData>
        data={filteredPackages}
        totalCount={filteredPackages.length}
        loading={loading}
        columns={columns}
        actions={actions}
        getRowId={(row) => row.packageId}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50]}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search packages..."
        onSearchChange={setSearchTerm}
        filters={filters}
        filterOptions={[
          {
            field: 'kind',
            label: 'Kind',
            type: 'select',
            options: [
              { value: dt.ENTERPRISE_PACKAGE_KIND.Pilot, label: 'Pilot' },
              { value: dt.ENTERPRISE_PACKAGE_KIND.Annual, label: 'Annual' },
              { value: dt.ENTERPRISE_PACKAGE_KIND.Amendment, label: 'Amendment' },
            ],
          },
          {
            field: 'assigned',
            label: 'Assignment',
            type: 'select',
            options: [
              { value: 'no', label: 'Available' },
              { value: 'yes', label: 'Assigned' },
            ],
          },
        ]}
        onFilterChange={(filters) => setFilters(filters as Record<string, string>)}
        onAdd={onAdd}
        addLabel={addLabel}
        emptyStateTitle="No enterprise packages"
        emptyStateDescription="Create a package to define pilot or annual deals before assigning them to tenants"
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />
    </Stack>
  );
};

export default EnterprisePackagesTable;
